import type {
  TeamInvitation,
  TeamManageableRole,
  TeamMember,
  TeamMemberRole,
} from "@vekha/contracts";

export const DEFAULT_INVITE_ROLE: TeamManageableRole = "manager";

export type PublicTeamAccessKind = "invitation" | "password-reset";

export type TeamMemberPermissions = Readonly<{
  canChangeRole: boolean;
  canDeactivate: boolean;
  canResetPassword: boolean;
  canReprovision: boolean;
  roleOptions: TeamManageableRole[];
  explanation: string | null;
}>;

export type SemanticTeamIdempotency = Readonly<{
  forSemantic: (semantic: string) => string;
  reset: () => void;
}>;

export function publicTeamAccessKind(pathname: string): PublicTeamAccessKind | null {
  if (pathname === "/accept-invitation") return "invitation";
  if (pathname === "/reset-password") return "password-reset";
  return null;
}

export function teamInvitationAcceptanceUrl(origin: string): string {
  return new URL("/accept-invitation", origin).toString();
}

export function canManageTeamRole(
  role: TeamMemberRole | null | undefined,
): role is "owner" | "admin" {
  return role === "owner" || role === "admin";
}

export function teamRoleLabel(role: TeamMemberRole): string {
  switch (role) {
    case "owner":
      return "Владелец";
    case "admin":
      return "Администратор";
    case "manager":
      return "Оператор";
    case "reviewer":
      return "Наблюдатель";
    default: {
      const exhaustive: never = role;
      return exhaustive;
    }
  }
}

export function teamRoleSummary(role: TeamMemberRole): string {
  switch (role) {
    case "owner":
      return "Полные права: рабочие диалоги, сценарии, подключения и доступы.";
    case "admin":
      return "Рабочие диалоги, сценарии, подключения и доступы. Без смены владельца.";
    case "manager":
      return "Рабочие диалоги: проверка, перегенерация и обучение. Без сценариев, подключений и доступов.";
    case "reviewer":
      return "Просмотр диалогов, правка и подтверждение ответов. Без управления сценариями, подключениями и доступами.";
    default: {
      const exhaustive: never = role;
      return exhaustive;
    }
  }
}

export function inviteRoleOptions(actorRole: "owner" | "admin"): TeamManageableRole[] {
  return actorRole === "owner" ? ["manager", "reviewer", "admin"] : ["manager", "reviewer"];
}

export function defaultInviteReason(role: TeamManageableRole): string {
  switch (role) {
    case "admin":
      return "Доступ администратору для рабочих диалогов, сценариев и выдачи доступов.";
    case "reviewer":
      return "Доступ наблюдателю для правки и проверки ответов.";
    case "manager":
      return "Доступ оператору для работы с диалогами, перегенерации и обучения модели.";
    default: {
      const exhaustive: never = role;
      return exhaustive;
    }
  }
}

export function teamMemberState(member: TeamMember): {
  label: string;
  tone: "safe" | "warning" | "neutral";
} {
  if (member.state === "restore_reprovision_required") {
    return { label: "Нужен новый доступ", tone: "warning" };
  }
  if (member.state === "inactive") return { label: "Отключён", tone: "neutral" };
  if (member.state === "password_reset_required") {
    return { label: "Ждёт новый пароль", tone: "warning" };
  }
  return { label: "Активен", tone: "safe" };
}

export function teamMemberPermissions(
  actorRole: "owner" | "admin",
  actorUserId: string,
  member: TeamMember,
): TeamMemberPermissions {
  if (member.userId === actorUserId) {
    return noPermissions("Свою роль и доступ нельзя менять из списка команды.");
  }
  if (member.role === "owner") {
    return noPermissions("Учётная запись владельца защищена от изменений в интерфейсе.");
  }
  if (member.state === "restore_reprovision_required") {
    return actorRole === "owner"
      ? {
          canChangeRole: false,
          canDeactivate: false,
          canResetPassword: false,
          canReprovision: true,
          roleOptions: ["admin", "manager", "reviewer"],
          explanation: "После восстановления старые пароль, сессии и второй фактор недействительны. Выдайте новый одноразовый доступ.",
        }
      : noPermissions("Новый доступ после восстановления может выдать только владелец.");
  }
  if (member.state === "inactive") {
    return noPermissions("Доступ уже отключён. Повторная активация в этой версии не предусмотрена.");
  }
  if (actorRole === "admin" && member.role === "admin") {
    return noPermissions("Доступ другого администратора может изменить только владелец.");
  }

  return {
    canChangeRole: true,
    canDeactivate: true,
    canResetPassword: true,
    canReprovision: false,
    roleOptions: actorRole === "owner"
      ? ["admin", "manager", "reviewer"]
      : ["manager", "reviewer"],
    explanation: null,
  };
}

export function canManageInvitation(
  actorRole: "owner" | "admin",
  invitation: TeamInvitation,
): boolean {
  if (invitation.purpose === "restore_reprovision") return actorRole === "owner";
  return actorRole === "owner" || invitation.role !== "admin";
}

export function createTeamIdempotencyKey(
  fillRandom?: (bytes: Uint8Array<ArrayBuffer>) => void,
): string {
  const bytes = new Uint8Array(new ArrayBuffer(32));
  if (fillRandom) fillRandom(bytes);
  else crypto.getRandomValues(bytes);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

export function createSemanticTeamIdempotency(
  generateKey: () => string = createTeamIdempotencyKey,
): SemanticTeamIdempotency {
  let current: { semantic: string; key: string } | null = null;
  return {
    forSemantic(semantic) {
      if (!current || current.semantic !== semantic) {
        current = { semantic, key: generateKey() };
      }
      return current.key;
    },
    reset() {
      current = null;
    },
  };
}

function noPermissions(explanation: string): TeamMemberPermissions {
  return {
    canChangeRole: false,
    canDeactivate: false,
    canResetPassword: false,
    canReprovision: false,
    roleOptions: [],
    explanation,
  };
}
